import { useMemo } from 'react';
import { InterviewData, InterviewRank } from '@/lib/interviewTypes';
import {
    calculateRanksFromAttempts,
    filterInterviewsForStudent,
    getStudentScoredInterviews,
    sortInterviewsByDate,
    hasAnyRank
} from '@/lib/interviewUtils';

interface UseInterviewDataProps {
    interviews: InterviewData[] | undefined;
    studentId?: string;
    studentAttempts?: any[];
    sortAscending?: boolean;
}

interface UseInterviewDataReturn {
    allInterviews: InterviewData[];
    studentInterviews: InterviewData[];
    ranks: InterviewRank;
    hasAnyRank: boolean;
    isLoading: boolean;
}

export const useInterviewData = ({
    interviews,
    studentId,
    studentAttempts,
    sortAscending = false
}: UseInterviewDataProps): UseInterviewDataReturn => {
    return useMemo(() => {
        const defaultRanks: InterviewRank = {
            html_css: 0,
            javascript: 0,
            react: 0,
            backend: 0
        };

        if (!interviews || interviews.length === 0) {
            return {
                allInterviews: [],
                studentInterviews: [],
                ranks: defaultRanks,
                hasAnyRank: false,
                isLoading: false
            };
        }

        const filteredInterviews = studentId
            ? filterInterviewsForStudent(interviews, studentId)
            : interviews;

        const attempts = Array.isArray(studentAttempts) ? studentAttempts : [];

        const attemptMap = new Map<string, any>();
        attempts.forEach((attempt: any) => {
            const ref = attempt?.interviewId;
            const key =
                (typeof ref === 'object' && ref?._id
                    ? ref._id.toString()
                    : ref?.toString()) || '';
            if (key) {
                attemptMap.set(key, attempt);
            }
        });

        const mergedInterviews = filteredInterviews.map((interview) => {
            const attempt = attemptMap.get(interview._id?.toString());
            if (!attempt) {
                return interview;
            }

            const moduleNumber =
                attempt?.moduleNumber ??
                attempt?.interviewId?.moduleNumber ??
                interview.moduleNumber;

            return {
                ...interview,
                moduleNumber,
                status: attempt?.status || interview.status,
                overallScore: attempt?.overallScore ?? interview.overallScore,
                technicalScore:
                    attempt?.technicalScore ?? interview.technicalScore,
                communicationScore:
                    attempt?.communicationScore ?? interview.communicationScore,
                problemSolvingScore:
                    attempt?.problemSolvingScore ?? interview.problemSolvingScore,
                feedback: attempt?.feedback ?? interview.feedback,
                studentAttempt: attempt,
            };
        });

        const studentScoredInterviews = studentId
            ? getStudentScoredInterviews(interviews, studentId)
            : [];

        const ranksSource = attempts.length > 0 ? attempts : studentScoredInterviews;

        const ranks =
            ranksSource.length > 0
                ? calculateRanksFromAttempts(ranksSource)
                : defaultRanks;

        const sortedInterviews = sortInterviewsByDate(mergedInterviews, sortAscending);

        return {
            allInterviews: interviews,
            studentInterviews: sortedInterviews,
            ranks,
            hasAnyRank: hasAnyRank(ranks),
            isLoading: false
        };
    }, [interviews, studentAttempts, studentId, sortAscending]);
};
